(() => {
  const readyClass = 'video-ready';
  const PRIMARY_PREVIEW_COUNT = 3;
  const PDP_PREVIEW_BUFFER_SECONDS = 3;
  const PDP_PREVIEW_BUFFER_TIMEOUT_MS = 12000;
  const GOOD_CONNECTION_MIN_MBPS = 5;
  const PRIMARY_PLAYBACK_WATCHDOG_MS = 1200;
  const PRIMARY_STALL_TICKS = 3;
  const MOBILE_FOCUS_SCALE_DELTA = 0.1;
  const DESKTOP_FOCUS_SCALE_DELTA = 0.04;
  const DESKTOP_MQ = '(min-width: 990px)';
  const TAP_MOVE_THRESHOLD = 10;
  const SWIPE_DISTANCE_THRESHOLD = 36;
  const SWIPE_VELOCITY_THRESHOLD = 0.25;
  const SLIDE_SNAP_DURATION = 240;
  // Loading order (Z-40): hero image first, then the first three cards, then the rest staggered.
  // Within NEAR_MARGIN_PX of the viewport every loaded card keeps playing; only farther away
  // may a card pause, and it resumes as soon as the block re-enters this lead zone.
  const NEAR_MARGIN_PX = 1500;
  const HERO_FALLBACK_MS = 2500;
  const HOMEPAGE_HAIR_WAIT_MAX_MS = 4000;
  const PRIMARY_HEAD_START_MAX_MS = 1500;
  const SECONDARY_STAGGER_MS = 400;
  const NEAR_STAGGER_MS = 120;
  // Z-58: the TikTok in-app browser on iOS cannot play inline (flag set in layout/theme.liquid).
  // There no card preview loads or plays, so nothing pops into the fullscreen player; cards keep
  // their posters, and only a tap (modal) starts a video.
  const noInlineVideo = () => window.zadeyraNoInlineVideo === true;

  // --- Centralized visibility handler (one listener, not per-block) ---
  const visibilityCallbacks = new Set();
  let visibilityListenerAdded = false;

  const onVisibilityChange = (cb) => {
    visibilityCallbacks.add(cb);
    if (!visibilityListenerAdded) {
      visibilityListenerAdded = true;
      document.addEventListener('visibilitychange', () => {
        const hidden = document.hidden;
        visibilityCallbacks.forEach((callback) => callback(hidden));
      });
    }
    return () => visibilityCallbacks.delete(cb);
  };

  const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  // The page hero is the first *visible* fetchpriority="high" image (product gallery image or
  // homepage campaign image). Hidden variants are skipped: on the product page the mobile hero
  // sits in a <picture> whose desktop source is a 1px data: GIF, and the desktop gallery is
  // mounted by script, so the real desktop hero can appear a little after this file runs.
  const HERO_LOOKUP_INTERVAL_MS = 100;
  const isPlaceholderSource = (image) => /^data:/i.test(image.currentSrc || image.getAttribute('src') || '');
  const isVisibleHero = (image) => {
    if (!image || !image.isConnected) return false;
    const rect = image.getBoundingClientRect();
    if (rect.width <= 1 || rect.height <= 1) return false;
    // currentSrc is empty until the source is chosen; a chosen data: GIF is the hidden variant.
    return !(image.currentSrc && isPlaceholderSource(image));
  };
  const findVisibleHero = () => Array.from(document.querySelectorAll('img[fetchpriority="high"]')).find(isVisibleHero) || null;

  // Resolves once the visible hero has loaded, or window.load fired, or after a fallback timeout.
  // Only the product page mounts its (desktop) hero by script after this file runs. Elsewhere
  // (homepage, collection pages) the hero is in the server HTML: if the page has no
  // fetchpriority="high" image at all, there is nothing to wait for (Z-56).
  let heroReadyPromise = null;
  const whenHeroReady = (heroMayMountLater = true) => {
    if (heroReadyPromise) return heroReadyPromise;
    heroReadyPromise = new Promise((resolve) => {
      if (document.readyState === 'complete') {
        resolve();
        return;
      }
      let done = false;
      let lookupTimer = null;
      const finish = () => {
        if (done) return;
        done = true;
        window.clearTimeout(lookupTimer);
        resolve();
      };
      window.addEventListener('load', finish, { once: true });
      window.setTimeout(finish, HERO_FALLBACK_MS);

      const watchHero = () => {
        if (done) return;
        const hero = findVisibleHero();
        if (!hero) {
          if (!heroMayMountLater && !document.querySelector('img[fetchpriority="high"]')) {
            finish();
            return;
          }
          // Not mounted or laid out yet: look again shortly (bounded by the fallback timeout).
          lookupTimer = window.setTimeout(watchHero, HERO_LOOKUP_INTERVAL_MS);
          return;
        }
        if (hero.complete && hero.naturalWidth > 1 && !isPlaceholderSource(hero)) {
          finish();
          return;
        }
        const onSettled = () => {
          // A <picture> may settle on its placeholder first; then keep looking for the real hero.
          if (isPlaceholderSource(hero) || hero.naturalWidth <= 1) {
            lookupTimer = window.setTimeout(watchHero, HERO_LOOKUP_INTERVAL_MS);
            return;
          }
          finish();
        };
        hero.addEventListener('load', onSettled, { once: true });
        hero.addEventListener('error', finish, { once: true });
      };
      watchHero();
    });
    return heroReadyPromise;
  };

  // iOS only autoplays inline video that is muted + playsinline. The autoplay attribute is
  // removed on purpose: Chrome (Android) and WebKit pause attribute-autoplayed muted videos
  // whenever they leave the viewport, including cards scrolled sideways out of the slider.
  // Playback is started exclusively via play() below.
  const prepareInlineVideo = (video) => {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.loop = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    if (video.autoplay) video.autoplay = false;
  };

  const hydrateDeferredImage = (image) => {
    if (!image) return Promise.resolve();
    if (image._ugcHydrationPromise) return image._ugcHydrationPromise;
    // Lazy card posters are promoted once their video starts loading, so the poster is there
    // before the first video frame.
    if (image.loading === 'lazy') image.loading = 'eager';

    if (image.dataset.src) {
      if (image.dataset.srcset) image.srcset = image.dataset.srcset;
      if (image.dataset.sizes) image.sizes = image.dataset.sizes;
      image.src = image.dataset.src;
      delete image.dataset.src;
      delete image.dataset.srcset;
      delete image.dataset.sizes;
    }

    if (!image.currentSrc && !image.src) return Promise.resolve();

    image._ugcHydrationPromise = new Promise((resolve) => {
      if (image.complete && image.naturalWidth > 1) {
        resolve();
        return;
      }
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', resolve, { once: true });
    }).then(() => {
      if (typeof image.decode === 'function') return image.decode().catch(() => {});
    });

    return image._ugcHydrationPromise;
  };

  const hasVideoSource = (video) => Boolean(
    video?.currentSrc
    || video?.src
    || video?.querySelector('source[src]')
  );

  // --- Safari-safe video recovery ---
  // Pauses we trigger ourselves (far away, modal restart, recovery nudge) set video._ugcSelfPause
  // first. Every other pause (iOS interruption, Low Power Mode, OS media session, offscreen
  // policies) is answered with an immediate play() while the video should be playing.
  const pauseVideoQuietly = (video) => {
    if (!video || video.paused) return;
    video._ugcSelfPause = true;
    video.pause();
    video._ugcSelfPause = false;
  };

  const attachRecovery = (video, shouldBePlayingFn, onBufferingChange = () => {}) => {
    // Without inline playback a restart would reopen the fullscreen player (e.g. after closing it).
    if (noInlineVideo()) return { ensurePlaying: () => {}, hardRecover: () => {} };
    let recoveryTimer = null;
    let recoveryInProgress = false;
    let consecutiveSoftRecoveries = 0;
    let lastImmediateRestart = 0;

    const isProgressing = () => !video.paused && !video.ended && video.readyState >= 3;

    const playIfWanted = () => {
      if (document.hidden || !shouldBePlayingFn() || !hasVideoSource(video)) return;
      if (!video.paused) return;
      const playback = video.play();
      if (playback && typeof playback.catch === 'function') playback.catch(() => {});
    };

    const hardRecover = () => {
      if (recoveryInProgress || document.hidden || !shouldBePlayingFn() || !hasVideoSource(video)) return;
      recoveryInProgress = true;
      onBufferingChange(true);

      const resumeTime = Number.isFinite(video.currentTime) ? video.currentTime : 0;
      const finishRecovery = () => {
        recoveryInProgress = false;
        consecutiveSoftRecoveries = 0;
        playIfWanted();
      };

      // Without a real media error the loaded source is kept: only a tiny seek nudges a frozen
      // decoder. load() (which drops the buffer) is reserved for genuine errors.
      if (!video.error && video.networkState !== HTMLMediaElement.NETWORK_NO_SOURCE) {
        pauseVideoQuietly(video);
        if (video.readyState >= 1 && Number.isFinite(video.duration) && video.duration > 0) {
          try {
            const maxTime = Math.max(0, video.duration - 0.05);
            video.currentTime = resumeTime >= maxTime
              ? 0
              : Math.min(resumeTime + 0.01, maxTime);
          } catch (error) {}
        }
        finishRecovery();
        return;
      }

      video.addEventListener('loadedmetadata', () => {
        if (resumeTime > 0 && Number.isFinite(video.duration)) {
          try {
            video.currentTime = Math.min(resumeTime, Math.max(0, video.duration - 0.1));
          } catch (error) {}
        }
        finishRecovery();
      }, { once: true });
      video.load();
      window.setTimeout(() => {
        if (recoveryInProgress) finishRecovery();
      }, 5000);
    };

    const attemptRecovery = (forceHard = false) => {
      clearTimeout(recoveryTimer);
      if (document.hidden) return;
      if (!shouldBePlayingFn()) return;

      recoveryTimer = setTimeout(() => {
        if (document.hidden || !shouldBePlayingFn()) return;
        if (!hasVideoSource(video)) return;
        // 'stalled' also fires for fully buffered, happily playing videos. Leave those alone.
        if (!forceHard && !video.error && isProgressing()) {
          consecutiveSoftRecoveries = 0;
          return;
        }
        if (forceHard
          || video.error
          || video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE
          || consecutiveSoftRecoveries >= 1) {
          hardRecover();
          return;
        }
        consecutiveSoftRecoveries++;
        playIfWanted();
      }, 350);
    };

    video.addEventListener('waiting', () => attemptRecovery());
    video.addEventListener('stalled', () => attemptRecovery());
    video.addEventListener('error', () => attemptRecovery());
    video.addEventListener('ended', () => attemptRecovery());
    video.addEventListener('playing', () => {
      clearTimeout(recoveryTimer);
      consecutiveSoftRecoveries = 0;
      onBufferingChange(false);
    });

    video.addEventListener('pause', () => {
      if (video._ugcSelfPause || recoveryInProgress) return;
      if (document.hidden) return;
      if (!shouldBePlayingFn()) return;
      // Foreign pause while the page is visible: restart synchronously. A second foreign pause
      // within 250 ms (e.g. Low Power Mode rejecting play) falls back to the timed recovery.
      const now = performance.now();
      if (now - lastImmediateRestart > 250) {
        lastImmediateRestart = now;
        playIfWanted();
        return;
      }
      attemptRecovery();
    });

    return {
      ensurePlaying: playIfWanted,
      hardRecover
    };
  };

  const waitForVideoPlayable = (video, timeout = 7000) => new Promise((resolve) => {
    if (video.readyState >= 2) {
      resolve(video);
      return;
    }

    let timer;
    const finish = () => {
      clearTimeout(timer);
      video.removeEventListener('loadeddata', finish);
      video.removeEventListener('canplay', finish);
      video.removeEventListener('error', finish);
      resolve(video);
    };
    video.addEventListener('loadeddata', finish, { once: true });
    video.addEventListener('canplay', finish, { once: true });
    video.addEventListener('error', finish, { once: true });
    timer = window.setTimeout(finish, timeout);
  });

  const bufferedSecondsAhead = (video) => {
    if (!video?.buffered?.length) return 0;
    for (let index = 0; index < video.buffered.length; index++) {
      if (video.buffered.start(index) <= video.currentTime + 0.05
        && video.buffered.end(index) >= video.currentTime) {
        return Math.max(0, video.buffered.end(index) - video.currentTime);
      }
    }
    return 0;
  };

  const waitForStablePreviewBuffer = (
    video,
    seconds = PDP_PREVIEW_BUFFER_SECONDS,
    timeout = PDP_PREVIEW_BUFFER_TIMEOUT_MS
  ) => new Promise((resolve) => {
    const startedAt = performance.now();
    let timer;
    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;
      clearInterval(timer);
      resolve(video);
    };
    const check = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      const target = duration > 0 ? Math.min(seconds, Math.max(0.5, duration - 0.25)) : seconds;
      if (bufferedSecondsAhead(video) >= target || performance.now() - startedAt >= timeout || video.error) {
        finish();
      }
    };

    timer = window.setInterval(check, 250);
    check();
  });

  // --- Preview video initialization ---
  // Runs exactly once per video: sources and preload are never reset afterwards.
  const initPreviewVideo = (card, immediate = false) => {
    const src = card.dataset.videoSrc;
    const video = card._ugcVideo || card.querySelector('video');
    if (!src || !video) return Promise.resolve(video);
    if (video._ugcReadyPromise) return video._ugcReadyPromise;

    video.dataset.loaded = 'true';
    prepareInlineVideo(video);
    const hasNativeSource = hasVideoSource(video);
    video.preload = 'auto';
    // Cards play the original MP4 (Z-55, as before Z-40): the same file the modal shows, so the
    // card video can be moved into the modal without a source change or visible quality step.
    if (!hasNativeSource) {
      const mp4Source = document.createElement('source');
      mp4Source.src = src;
      mp4Source.type = 'video/mp4';
      video.appendChild(mp4Source);
    }

    if (!video._ugcPreviewEventsReady) {
      video._ugcPreviewEventsReady = true;
      let frameQueued = false;
      const markReady = () => {
        if (frameQueued || card.classList.contains(readyClass)) return;
        frameQueued = true;
        const reveal = () => {
          frameQueued = false;
          if (!video.paused && video.readyState >= 2) card.classList.add(readyClass);
        };
        if (typeof video.requestVideoFrameCallback === 'function') {
          video.requestVideoFrameCallback(reveal);
        } else {
          requestAnimationFrame(() => requestAnimationFrame(reveal));
        }
      };

      video.addEventListener('playing', markReady);
      // A running video must never stay hidden behind its poster: re-reveal on progress.
      video.addEventListener('timeupdate', () => {
        if (!card.classList.contains(readyClass) && !video.paused && video.readyState >= 3) markReady();
      });
      // Only real buffering hides the frame; 'stalled' fires for healthy fully buffered videos too.
      video.addEventListener('waiting', () => card.classList.remove(readyClass));
    }
    hydrateDeferredImage(card.querySelector('.ugc-poster')).catch(() => {});
    if (!hasNativeSource || video.networkState === HTMLMediaElement.NETWORK_EMPTY) video.load();
    video._ugcPlayablePromise = waitForVideoPlayable(video, immediate ? 8000 : 7000);
    video._ugcReadyPromise = video._ugcPlayablePromise
      .then(() => immediate ? video : waitForStablePreviewBuffer(video))
      .then(() => {
        video._ugcPrimed = true;
        return video;
      });
    return video._ugcReadyPromise;
  };

  // --- Block initialization ---
  const initBlock = (block) => {
    if (block.dataset.ugcInit === 'true') return;
    block.dataset.ugcInit = 'true';

    const cards = Array.from(block.querySelectorAll('.ugc-card'));
    if (!cards.length) return;
    const previewVideos = cards.map((card) => {
      const video = card.querySelector('video');
      card._ugcVideo = video;
      if (video) prepareInlineVideo(video);
      return video;
    });
    // One controller per instance: a re-rendered section (theme editor) tears the old one down,
    // so no orphaned listeners or intervals keep calling play() on detached videos.
    const instanceAbort = typeof AbortController === 'function' ? new AbortController() : null;
    const listenerOptions = (options = {}) => (instanceAbort ? { ...options, signal: instanceAbort.signal } : options);
    let tornDown = false;
    const row = block.querySelector('.ugc-row');
    const cardShells = cards.map((card) => card.closest('.ugc-card-shell'));
    const isHomepage = block.dataset.pageType === 'index';
    const isProductPage = block.dataset.pageType === 'product';
    const isCollectionPage = block.dataset.pageType === 'collection';
    const autoplayPreviewCount = isHomepage || isProductPage || isCollectionPage
      ? cards.length
      : PRIMARY_PREVIEW_COUNT;
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const canWarmInParallel = Boolean(
      connection
      && !connection.saveData
      && connection.effectiveType === '4g'
      && Number(connection.downlink || 0) >= GOOD_CONNECTION_MIN_MBPS
    );

    const hydratePreviewNeighborhood = (index) => {
      const first = Math.max(0, index - 1);
      const last = Math.min(cards.length - 1, index + 1);
      for (let currentIndex = first; currentIndex <= last; currentIndex++) {
        hydrateDeferredImage(cards[currentIndex]?.querySelector('.ugc-poster'));
      }
    };

    cards.forEach((card, index) => {
      card.dataset.index = String(index);
    });

    const modal = block.querySelector('.ugc-modal');
    const slider = modal?.querySelector('.ugc-slider');
    const slideContainers = slider ? Array.from(slider.querySelectorAll('.ugc-slide')) : [];
    const hydrateModalNeighborhood = (index) => {
      const first = Math.max(0, index - 1);
      const last = Math.min(slideContainers.length - 1, index + 1);
      for (let currentIndex = first; currentIndex <= last; currentIndex++) {
        hydrateDeferredImage(slideContainers[currentIndex]?.querySelector('.ugc-modal-poster'));
      }
    };
    const closeBtn = modal?.querySelector('.ugc-close');
    const prevBtn = modal?.querySelector('.ugc-prev');
    const nextBtn = modal?.querySelector('.ugc-next');
    const clipDetails = modal ? Array.from(modal.querySelectorAll('.ugc-clip-details[data-slide-index]')) : [];
    let opener = null;
    let previousOverflow = '';
    const syncDetails = () => clipDetails.forEach((detail) => {
      const hidden = detail.dataset.slideIndex !== String(current);
      if (hidden && detail.contains(document.activeElement)) closeBtn.focus({ preventScroll: true });
      detail.hidden = hidden;
    });

    if (!modal || !slider || !slideContainers.length || !closeBtn || !prevBtn || !nextBtn) return;

    const previewRootMargin = `${NEAR_MARGIN_PX}px 0px`;
    const loadDelay = Math.min(2000, Math.max(0, Number.parseInt(block.dataset.loadDelay, 10) || 0));

    let current = 0;
    let muted = true;
    let modalOpen = false;
    const externalOverlays = new Set();
    const hasExternalOverlay = () => externalOverlays.size > 0;
    let blockNearViewport = false;
    let blockVisible = false;
    let focusFrame = null;
    let focusActivated = true;
    let rowWidth = 0;
    let maxScrollLeft = 0;
    let averageStep = 0;
    let shellWidths = [];
    let shellCenters = [];
    let rowPointerStart = null;
    let rowWasDragged = false;
    let modalPointerStart = null;
    let modalWasDragged = false;
    let modalDragFrame = null;
    let pendingModalDeltaX = 0;
    let slideTransitioning = false;
    let slideTransitionTimer = null;

    // Desktop uses a subtler zoom than mobile; recomputed on resize.
    let focusScaleDelta = window.matchMedia(DESKTOP_MQ).matches
      ? DESKTOP_FOCUS_SCALE_DELTA
      : MOBILE_FOCUS_SCALE_DELTA;

    // Track the video currently re-parented into the modal
    let activeModalVideo = null;
    let activeModalVideoCard = null;
    let visibilityFrame = null;

    const muteEveryOtherUgcVideo = (activeVideo) => {
      document.querySelectorAll('[data-ugc-reels] video').forEach((video) => {
        if (video !== activeVideo) video.muted = true;
      });
    };

    const enableActiveVideoSound = async () => {
      if (!activeModalVideo) return;

      muteEveryOtherUgcVideo(activeModalVideo);
      muted = false;
      activeModalVideo.muted = false;
      activeModalVideo.volume = 1;

      try {
        await activeModalVideo.play();
      } catch (error) {
        console.error('UGC video could not play with sound:', error);
      }
    };

    // Track which card index is closest to center (for z-index updates)
    let centeredIndex = -1;

    // --- Playback policy ---
    // A loaded card plays whenever the block is within NEAR_MARGIN_PX of the viewport, no matter
    // whether the card is scrolled sideways out of the slider. Farther away it may pause, but only
    // after it has buffered (so iOS has really fetched it); it resumes on re-entering the lead zone.
    const previewShouldPlay = (index) => {
      const video = previewVideos[index];
      if (tornDown || !video || video.dataset.loaded !== 'true' || document.hidden) return false;
      if (modalOpen && activeModalVideo === video) return true;
      if (noInlineVideo()) return false;
      if (blockNearViewport) return true;
      return !video._ugcPrimed;
    };

    // Synchronous play() on every card that should be playing: used for tab/app return,
    // bfcache restore, focus and user interaction, without timers and without touching sources.
    const playEligibleNow = () => {
      if (tornDown || document.hidden || noInlineVideo()) return;
      previewVideos.forEach((video, index) => {
        if (!video || !video.paused || !hasVideoSource(video)) return;
        if (!previewShouldPlay(index)) return;
        if (modalOpen && activeModalVideo === video) video.muted = muted;
        else prepareInlineVideo(video);
        const playback = video.play();
        if (playback && typeof playback.catch === 'function') playback.catch(() => {});
      });
    };

    const startPreviewVideo = (card, video) => {
      if (tornDown || !video || document.hidden || !hasVideoSource(video) || noInlineVideo()) return;
      if (modalOpen && activeModalVideo === video) return;
      if (video._ugcPlayPending) return;
      prepareInlineVideo(video);
      const playback = video.play();
      if (!playback || typeof playback.then !== 'function') return;
      video._ugcPlayPending = true;
      playback.then(() => {
        video._ugcPlayPending = false;
      }, () => {
        video._ugcPlayPending = false;
        window.setTimeout(() => {
          if (document.hidden || !video.paused || !hasVideoSource(video)) return;
          if (!previewShouldPlay(previewVideos.indexOf(video))) return;
          if (modalOpen && activeModalVideo === video) return;
          video.play().catch(() => {});
        }, 400);
      });
    };

    // Number of cards (in DOM order) that may load; raised step by step by the load sequence.
    let allowedCount = 0;

    const syncPreviewPlayback = () => {
      if (tornDown) return;
      cards.forEach((card, index) => {
        const video = previewVideos[index];
        if (!video) return;
        if (index >= allowedCount && video.dataset.loaded !== 'true') return;
        if (noInlineVideo()) {
          // Posters only: no preview is loaded; a video returned from the modal stays paused.
          if (!(modalOpen && activeModalVideo === video)) pauseVideoQuietly(video);
          return;
        }
        initPreviewVideo(card);
        if (modalOpen && activeModalVideo === video) return;
        if (!previewShouldPlay(index)) {
          // Only reached when the block is more than NEAR_MARGIN_PX away (or the tab is hidden).
          if (!document.hidden) pauseVideoQuietly(video);
          return;
        }
        if (video.paused) startPreviewVideo(card, video);
      });
    };

    const allowCardsUpTo = (count) => {
      const next = Math.min(autoplayPreviewCount, cards.length, count);
      if (next <= allowedCount) return;
      allowedCount = next;
      syncPreviewPlayback();
    };

    // --- Load sequence ---
    // 1. hero image loaded (or window.load / fallback), homepage additionally waits briefly for
    //    the first hair-in-motion video; 2. data-load-delay; 3. cards 1-3; 4. remaining cards
    //    every SECONDARY_STAGGER_MS. Near the block (lead zone) the waits collapse to
    //    NEAR_STAGGER_MS; once the block is actually on screen even the hero wait is skipped.
    let rushed = false;
    let resolveRush = () => {};
    const rushPromise = new Promise((resolve) => { resolveRush = resolve; });
    let resolveUrgent = () => {};
    const urgentPromise = new Promise((resolve) => { resolveUrgent = resolve; });
    const requestRush = () => {
      if (rushed) return;
      rushed = true;
      block.dataset.ugcWarmup = 'rush';
      resolveRush();
    };

    const waitForHomepageHair = () => {
      if (!isHomepage || !document.querySelector('hair-in-motion')) return Promise.resolve();
      const hairState = window.__zadeyraHairMotionWarmState;
      const canBegin = canWarmInParallel ? hairState?.primaryReady : hairState?.allReady;
      if (canBegin) return Promise.resolve();
      const eventName = canWarmInParallel ? 'zadeyra:hair-primary-ready' : 'zadeyra:hair-all-ready';
      return Promise.race([
        new Promise((resolve) => window.addEventListener(eventName, () => resolve(), listenerOptions({ once: true }))),
        wait(HOMEPAGE_HAIR_WAIT_MAX_MS)
      ]);
    };

    const staggerWait = (ms) => (rushed
      ? wait(NEAR_STAGGER_MS)
      : Promise.race([wait(ms), rushPromise.then(() => wait(NEAR_STAGGER_MS))]));

    let loadSequenceStarted = false;
    const runLoadSequence = async () => {
      if (loadSequenceStarted) return;
      loadSequenceStarted = true;
      block.dataset.ugcWarmup = 'waiting';
      await Promise.race([
        whenHeroReady(isProductPage).then(() => Promise.race([waitForHomepageHair(), rushPromise])),
        urgentPromise
      ]);
      if (!rushed && loadDelay > 0) await Promise.race([wait(loadDelay), rushPromise]);
      if (tornDown) return;
      if (!rushed) block.dataset.ugcWarmup = 'running';
      allowCardsUpTo(PRIMARY_PREVIEW_COUNT);
      if (!rushed) {
        const primaryPlayable = previewVideos
          .slice(0, PRIMARY_PREVIEW_COUNT)
          .map((video) => video?._ugcPlayablePromise || Promise.resolve());
        await Promise.race([Promise.all(primaryPlayable), wait(PRIMARY_HEAD_START_MAX_MS), rushPromise]);
      }
      for (let index = PRIMARY_PREVIEW_COUNT; index < autoplayPreviewCount; index++) {
        await staggerWait(SECONDARY_STAGGER_MS);
        if (tornDown) return;
        allowCardsUpTo(index + 1);
      }
      block.dataset.ugcWarmup = 'ready';
    };

    const refreshPlaybackVisibility = () => {
      visibilityFrame = null;
      if (tornDown) return;
      const blockRect = block.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const rendered = blockRect.width > 0 || blockRect.height > 0;
      blockVisible = rendered && blockRect.bottom > 0 && blockRect.top < viewportHeight;
      blockNearViewport = rendered
        && blockRect.bottom > -NEAR_MARGIN_PX
        && blockRect.top < viewportHeight + NEAR_MARGIN_PX;
      if (blockNearViewport) requestRush();
      if (blockVisible) resolveUrgent();
      syncPreviewPlayback();
    };

    const queuePlaybackVisibilityRefresh = () => {
      if (tornDown || visibilityFrame !== null) return;
      visibilityFrame = window.requestAnimationFrame(refreshPlaybackVisibility);
    };

    const setCardTransform = (cardShell, progress, offsetX) => {
      if (!cardShell) return;
      const scale = 1 + progress * focusScaleDelta;
      cardShell.style.transform = `translate3d(${offsetX}px, 0, 0)`;
      const inner = cardShell.querySelector('.ugc-card');
      if (inner) inner.style.transform = `scale(${scale})`;
    };

    const clearCardTransforms = () => {
      const prevCenter = centeredIndex;
      centeredIndex = -1;
      cardShells.forEach((cardShell, i) => {
        if (!cardShell) return;
        cardShell.style.transform = '';
        cardShell.style.willChange = '';
        cardShell.style.zIndex = '';
        const inner = cardShell.querySelector('.ugc-card');
        if (inner) inner.style.transform = '';
      });
    };

    const setFocusActivated = (value) => {
      focusActivated = value;
      block.dataset.ugcFocusActive = value ? 'true' : 'false';

      if (!value) {
        clearCardTransforms();
        return;
      }

      queueCenteredCardUpdate();
    };

    let rowLeftCache = 0;

    const measureCarousel = () => {
      if (!row || !cardShells.length) {
        rowWidth = 0;
        maxScrollLeft = 0;
        averageStep = 0;
        shellWidths = [];
        shellCenters = [];
        rowLeftCache = 0;
        return;
      }

      rowWidth = row.clientWidth;
      maxScrollLeft = Math.max(0, row.scrollWidth - row.clientWidth);
      rowLeftCache = row.getBoundingClientRect().left;
      shellWidths = cardShells.map((cardShell) => cardShell?.offsetWidth || 0);
      shellCenters = cardShells.map((cardShell, index) => {
        const width = shellWidths[index] || 0;
        return (cardShell?.offsetLeft || 0) + width / 2;
      });

      if (shellCenters.length <= 1) {
        averageStep = rowWidth / 3;
        return;
      }

      let totalStep = 0;
      for (let index = 1; index < shellCenters.length; index++) {
        totalStep += Math.abs(shellCenters[index] - shellCenters[index - 1]);
      }
      averageStep = totalStep / (shellCenters.length - 1);
    };

    const smoothstep = (min, max, value) => {
      if (min === max) return value >= max ? 1 : 0;
      const clamped = Math.min(1, Math.max(0, (value - min) / (max - min)));
      return clamped * clamped * (3 - 2 * clamped);
    };

    const updateCenteredCard = () => {
      focusFrame = null;
      if (!row || !cardShells.length || !focusActivated) {
        clearCardTransforms();
        return;
      }

      // Use cached rowLeft — no getBoundingClientRect in the hot path
      const rowCenter = rowLeftCache + rowWidth / 2;
      const scrollLeft = row.scrollLeft;
      const effectiveAverageStep = averageStep > 0 ? averageStep : rowWidth > 0 ? rowWidth / 3 : 200;
      const edgeRamp = Math.max(effectiveAverageStep, 1);
      const startBias = Math.max(0, 1 - scrollLeft / edgeRamp);
      const endBias = Math.max(0, 1 - (maxScrollLeft - scrollLeft) / edgeRamp);
      const biasedFocusCenter = rowCenter + (startBias - endBias) * effectiveAverageStep;
      const edgeFade = Math.min(
        1,
        Math.max(0, scrollLeft / edgeRamp),
        Math.max(0, (maxScrollLeft - scrollLeft) / edgeRamp)
      );

      // Single pass: compute progress, offsets, and find the new center card
      const count = cardShells.length;
      const progresses = new Array(count);
      let bestIndex = 0;
      let bestProgress = -1;

      for (let i = 0; i < count; i++) {
        const cardCenter = rowLeftCache + shellCenters[i] - scrollLeft;
        const normalizedDistance = Math.abs(cardCenter - biasedFocusCenter) / Math.max(effectiveAverageStep, 1);
        const p = (1 - smoothstep(0, 2, normalizedDistance)) * edgeFade;
        progresses[i] = p;
        if (p > bestProgress) {
          bestProgress = p;
          bestIndex = i;
        }
      }

      // Compute cumulative offsets (spacing compensation)
      const offsets = new Array(count);
      offsets[0] = 0;
      for (let i = 1; i < count; i++) {
        const prevExtra = (shellWidths[i - 1] || 0) * (progresses[i - 1] * focusScaleDelta);
        const currExtra = (shellWidths[i] || 0) * (progresses[i] * focusScaleDelta);
        offsets[i] = offsets[i - 1] + (prevExtra + currExtra) * 0.5;
      }

      let offsetSum = 0;
      for (let i = 0; i < count; i++) offsetSum += offsets[i];
      const avgOffset = offsetSum / count;

      // Update z-index and will-change only when the centered card changes
      if (bestIndex !== centeredIndex) {
        const prevCenter = centeredIndex;
        centeredIndex = bestIndex;
        if (blockNearViewport) hydratePreviewNeighborhood(bestIndex);

        // Clear old center neighborhood
        for (let i = Math.max(0, prevCenter - 1); i <= Math.min(count - 1, prevCenter + 1); i++) {
          if (cardShells[i]) {
            cardShells[i].style.zIndex = '';
            cardShells[i].style.willChange = '';
          }
        }

        // Promote new center neighborhood
        for (let i = Math.max(0, bestIndex - 1); i <= Math.min(count - 1, bestIndex + 1); i++) {
          if (cardShells[i]) {
            cardShells[i].style.willChange = 'transform';
            cardShells[i].style.zIndex = (i === bestIndex) ? '3' : '2';
          }
        }
        syncPreviewPlayback(bestIndex);
      }

      // Single write pass: set transform directly on each element
      for (let i = 0; i < count; i++) {
        setCardTransform(cardShells[i], progresses[i], offsets[i] - avgOffset);
      }
    };

    const queueCenteredCardUpdate = () => {
      if (!focusActivated) return;
      if (focusFrame !== null) return;
      focusFrame = window.requestAnimationFrame(updateCenteredCard);
    };

    const handleRowScroll = () => {
      if (!row || !focusActivated) return;
      queueCenteredCardUpdate();
      syncPreviewPlayback();
    };

    // --- Video re-parenting helpers ---
    const returnVideoToCard = () => {
      if (!activeModalVideo || !activeModalVideoCard) return;

      const returnedVideo = activeModalVideo;
      const returnedCard = activeModalVideoCard;
      const slideContainer = activeModalVideo.closest('.ugc-slide');
      if (slideContainer) slideContainer.classList.remove('is-ready');

      // Re-mute for preview playback
      returnedVideo.muted = true;
      returnedCard.appendChild(returnedVideo);
      activeModalVideo = null;
      activeModalVideoCard = null;
      if (noInlineVideo()) {
        // No preview loop here: stop the video and put the poster back over it.
        pauseVideoQuietly(returnedVideo);
        returnedCard.classList.remove(readyClass);
        return;
      }
      startPreviewVideo(returnedCard, returnedVideo);
    };

    const moveVideoToModal = (index, restartAtBeginning = false) => {
      // Return the previous video to its card and keep its preview loop running.
      returnVideoToCard();

      const card = cards[index];
      initPreviewVideo(card, true);
      const video = card?.querySelector('video');
      const slideContainer = slideContainers[index];

      if (!video || !slideContainer) return;

      activeModalVideo = video;
      activeModalVideoCard = card;

      // The initial card click keeps the preview's current playback position.
      // Subsequent modal navigation starts the destination video at frame zero.
      if (restartAtBeginning && video.readyState >= 1) {
        pauseVideoQuietly(video);
        try {
          video.currentTime = 0;
        } catch (error) {
          // Metadata can briefly become unavailable on memory-constrained Safari.
        }
      }

      // Keep the poster visible while a restarted video seeks to frame zero.
      // This prevents the previous preview frame flashing before playback begins.
      if (noInlineVideo()) {
        // The modal poster stays until the video really plays (a swipe is not a tap, so the
        // next slide may stay paused until the viewer taps it).
        slideContainer.classList.remove('is-ready');
        if (!video.paused) {
          slideContainer.classList.add('is-ready');
        } else {
          video.addEventListener('playing', () => {
            if (video.parentElement === slideContainer) slideContainer.classList.add('is-ready');
          }, { once: true });
        }
      } else if (restartAtBeginning && video.seeking) {
        slideContainer.classList.remove('is-ready');
        video.addEventListener('seeked', () => {
          if (video.parentElement === slideContainer) {
            slideContainer.classList.add('is-ready');
          }
        }, { once: true });
      } else {
        slideContainer.classList.add('is-ready');
      }

      video.style.transition = 'none';
      slideContainer.appendChild(video);
      // Restore transition on next frame to avoid forced reflow
      requestAnimationFrame(() => { video.style.transition = ''; });

      // Apply modal playback settings and ensure playback
      video.muted = muted;
      if (video.paused) {
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      }
    };

    // --- Modal open/close ---
    const modalParent = modal.parentNode;

    const openModal = (index, enableSound) => {
      opener = cards[index];
      previousOverflow = document.body.style.overflow;
      initPreviewVideo(cards[index], true);
      hydrateModalNeighborhood(index);
      current = index;
      modalOpen = true;
      muted = !enableSound;

      document.body.appendChild(modal);
      modal.classList.add('active');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      updateSlide();
      closeBtn.focus({ preventScroll: true });
      if (enableSound) enableActiveVideoSound();
    };

    const closeModal = () => {
      if (slideTransitioning) {
        clearTimeout(slideTransitionTimer);
        slideTransitioning = false;
        slider.style.transition = 'none';
        slider.style.transform = `translate3d(-${current * 100}%, 0, 0)`;
        moveVideoToModal(current, true);
      }

      modal.classList.remove('active');
      modal.setAttribute('aria-hidden', 'true');
      modalOpen = false;

      // Move video back to its card without interrupting its preview loop.
      returnVideoToCard();

      document.body.style.overflow = previousOverflow;
      modalParent.appendChild(modal);
      opener?.focus({ preventScroll: true });

      syncPreviewPlayback();
    };

    // --- Modal slide update ---
    const updateSlide = (restartAtBeginning = false) => {
      clearTimeout(slideTransitionTimer);
      slideTransitioning = false;
      slider.style.transition = 'none';
      slider.style.transform = `translate3d(-${current * 100}%, 0, 0)`;

      // Move the correct preview video into the current modal slide
      moveVideoToModal(current, restartAtBeginning);

      syncDetails();
    };

    const finishSlideTransition = () => {
      if (!slideTransitioning) return;
      clearTimeout(slideTransitionTimer);
      slideTransitioning = false;
      slider.style.transition = '';

      // Keep the outgoing video in place for the entire slide animation.
      // Re-parent only after the destination slide has settled.
      moveVideoToModal(current, true);

    };

    const animateToSlide = (nextIndex, duration = SLIDE_SNAP_DURATION) => {
      if (slideTransitioning || nextIndex === current) {
        slider.style.transition = `transform ${Math.min(duration, 180)}ms cubic-bezier(0.16, 1, 0.3, 1)`;
        slider.style.transform = `translate3d(-${current * 100}%, 0, 0)`;
        return;
      }

      const previousIndex = current;
      current = nextIndex;
      hydrateModalNeighborhood(current);

      syncDetails();

      // A wrap from the first to last slide (or vice versa) would cross every
      // intermediate slide. Switch instantly at that boundary instead.
      if (Math.abs(current - previousIndex) > 1) {
        updateSlide(true);
        return;
      }

      slideTransitioning = true;
      // Flush the final drag position before enabling the snap transition.
      // This avoids a one-frame jump when pointerup lands between animation frames.
      slider.getBoundingClientRect();
      slider.style.transition = `transform ${duration}ms cubic-bezier(0.16, 1, 0.3, 1)`;
      slider.style.transform = `translate3d(-${current * 100}%, 0, 0)`;

      slideTransitionTimer = window.setTimeout(finishSlideTransition, duration + 80);
    };

    slider.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'transform') finishSlideTransition();
    });

    // --- Return to the page: tab/app switch, bfcache restore, window focus, frozen-page resume ---
    // Identical event set for every autoplay video on the site (Z-40 UGC, Z-43 hair-in-motion):
    // visibilitychange (visible) + pageshow (incl. event.persisted) + focus (+ document resume).
    // The handler calls play() synchronously on every card in the lead zone: no timer, no load(),
    // no src/preload reset. The modal video keeps its own mute state.
    const resumePlaybackNow = () => {
      if (tornDown || document.hidden) return;
      playEligibleNow();
      queuePlaybackVisibilityRefresh();
    };

    const unregisterVisibility = onVisibilityChange((hidden) => {
      if (!hidden) resumePlaybackNow();
    });

    window.addEventListener('pageshow', resumePlaybackNow, listenerOptions());
    window.addEventListener('focus', resumePlaybackNow, listenerOptions());
    window.addEventListener('online', resumePlaybackNow, listenerOptions());
    document.addEventListener('resume', resumePlaybackNow, listenerOptions());
    // Autoplay can be refused (iOS Low Power Mode, data saver); any user gesture retries at once.
    ['touchend', 'click', 'keydown'].forEach((type) => {
      document.addEventListener(type, playEligibleNow, listenerOptions({ passive: true }));
    });
    document.addEventListener('zadeyra:media-overlay-open', (event) => {
      if (modalOpen) return;
      externalOverlays.add(event.detail?.overlay || event.target);
    }, listenerOptions());
    document.addEventListener('zadeyra:media-overlay-close', (event) => {
      externalOverlays.delete(event.detail?.overlay || event.target);
      resumePlaybackNow();
    }, listenerOptions());

    // --- Preview video recovery (also covers modal playback) ---
    const recoveryControllers = [];
    cards.forEach((card, index) => {
      const video = previewVideos[index];
      if (video) {
        recoveryControllers[index] = attachRecovery(
          video,
          () => previewShouldPlay(index),
          (buffering) => card.classList.toggle(readyClass, !buffering)
        );
      }
    });

    const playbackStates = cards.map(() => ({
      frozenTicks: 0,
      lastTime: -1
    }));

    let blockObserver = null;
    let playbackObserver = null;
    let watchdogTimer = null;

    const teardown = () => {
      if (tornDown) return;
      tornDown = true;
      instanceAbort?.abort();
      unregisterVisibility();
      if (watchdogTimer !== null) window.clearInterval(watchdogTimer);
      blockObserver?.disconnect();
      playbackObserver?.disconnect();
      if (visibilityFrame !== null) window.cancelAnimationFrame(visibilityFrame);
      previewVideos.forEach((video) => pauseVideoQuietly(video));
    };

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target instanceof Node && event.target.contains(block)) teardown();
    }, listenerOptions());

    // Watchdog: restarts paused cards that should play, nudges frozen ones, and pauses cards
    // only when the block is farther than NEAR_MARGIN_PX away.
    watchdogTimer = window.setInterval(() => {
      if (!block.isConnected) {
        teardown();
        return;
      }
      if (document.hidden || noInlineVideo()) return;
      for (let index = 0; index < cards.length; index++) {
        const video = previewVideos[index];
        const playbackState = playbackStates[index];
        if (!video || video.dataset.loaded !== 'true' || !hasVideoSource(video)) continue;

        if (!previewShouldPlay(index)) {
          if (!(modalOpen && activeModalVideo === video)) pauseVideoQuietly(video);
          playbackState.frozenTicks = 0;
          playbackState.lastTime = -1;
          continue;
        }

        const currentTime = video.currentTime;
        const progressed = playbackState.lastTime < 0
          || Math.abs(currentTime - playbackState.lastTime) >= 0.08;

        if (video.paused) {
          playbackState.frozenTicks = 0;
          if (modalOpen && activeModalVideo === video) video.muted = muted;
          video.play().catch(() => {});
        } else if (video.readyState >= 2 && !progressed) {
          playbackState.frozenTicks++;
          if (playbackState.frozenTicks >= PRIMARY_STALL_TICKS) {
            recoveryControllers[index]?.hardRecover();
            playbackState.frozenTicks = 0;
          }
        } else {
          playbackState.frozenTicks = 0;
        }

        playbackState.lastTime = currentTime;
      }
    }, PRIMARY_PLAYBACK_WATCHDOG_MS);

    // --- Block viewport observers (the scroll handler computes the same zones as a fallback) ---
    blockObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        blockNearViewport = entry.isIntersecting;
        if (entry.isIntersecting) {
          requestRush();
          measureCarousel();
          hydratePreviewNeighborhood(centeredIndex >= 0 ? centeredIndex : 0);
          queueCenteredCardUpdate();
          // Re-entering the lead zone: start playback right here, not one frame later.
          syncPreviewPlayback();
        }
        queuePlaybackVisibilityRefresh();
      });
    }, { rootMargin: previewRootMargin, threshold: 0 });

    playbackObserver = new IntersectionObserver((entries) => {
      blockVisible = entries[0]?.isIntersecting === true;
      if (blockVisible) resolveUrgent();
      queuePlaybackVisibilityRefresh();
    }, { threshold: 0.01 });

    // --- Card event listeners ---
    cards.forEach((card) => {
      card.tabIndex = 0;
      card.setAttribute('role', 'button');
      card.setAttribute('aria-label', `Video ${Number(card.dataset.index) + 1} ansehen`);
      card.setAttribute('aria-haspopup', 'dialog');
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openModal(Number(card.dataset.index), true); }
      });
      card.addEventListener('click', (event) => {
        if (rowWasDragged) {
          event.preventDefault();
          return;
        }
        openModal(Number(card.dataset.index || 0), true);
      });
    });

    if (row) {
      measureCarousel();
      setFocusActivated(true);
      row.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary) return;
        rowPointerStart = { x: event.clientX, y: event.clientY };
        rowWasDragged = false;
      }, { passive: true });
      row.addEventListener('pointermove', (event) => {
        if (!rowPointerStart || !event.isPrimary) return;
        const distanceX = Math.abs(event.clientX - rowPointerStart.x);
        const distanceY = Math.abs(event.clientY - rowPointerStart.y);
        if (distanceX > TAP_MOVE_THRESHOLD || distanceY > TAP_MOVE_THRESHOLD) {
          rowWasDragged = true;
        }
      }, { passive: true });
      const finishRowPointer = () => {
        rowPointerStart = null;
        window.setTimeout(() => {
          rowWasDragged = false;
        }, 0);
      };
      row.addEventListener('pointerup', finishRowPointer, { passive: true });
      row.addEventListener('pointercancel', finishRowPointer, { passive: true });
      row.addEventListener('scroll', handleRowScroll, { passive: true });
      window.addEventListener('resize', () => {
        focusScaleDelta = window.matchMedia(DESKTOP_MQ).matches
          ? DESKTOP_FOCUS_SCALE_DELTA
          : MOBILE_FOCUS_SCALE_DELTA;
        measureCarousel();
        centeredIndex = -1;
        setFocusActivated(true);
        queueCenteredCardUpdate();
      }, listenerOptions({ passive: true }));
      queueCenteredCardUpdate();
    }

    window.addEventListener('scroll', queuePlaybackVisibilityRefresh, listenerOptions({ passive: true }));
    window.addEventListener('resize', queuePlaybackVisibilityRefresh, listenerOptions({ passive: true }));

    blockObserver.observe(block);
    playbackObserver.observe(block);
    runLoadSequence();
    queuePlaybackVisibilityRefresh();

    // Keep keyboard navigation and focus within the open viewer.
    document.addEventListener('keydown', (event) => {
      if (!modalOpen) return;
      if (event.key === 'Escape') { event.preventDefault(); closeModal(); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); prevBtn.click(); }
      if (event.key === 'ArrowRight') { event.preventDefault(); nextBtn.click(); }
      if (event.key === 'Tab') {
        const items = Array.from(modal.querySelectorAll('button, a[href]')).filter((el) => el.getClientRects().length);
        const first = items[0], last = items[items.length - 1];
        if (!modal.contains(document.activeElement)) { event.preventDefault(); first.focus(); }
        else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    }, listenerOptions());

    // --- Modal controls ---
    closeBtn.addEventListener('click', closeModal);

    prevBtn.addEventListener('click', () => {
      animateToSlide((current - 1 + slideContainers.length) % slideContainers.length);
    });

    nextBtn.addEventListener('click', () => {
      animateToSlide((current + 1) % slideContainers.length);
    });

    slider.addEventListener('pointerdown', (event) => {
      if (slideTransitioning || !event.isPrimary || !event.target.closest('video')) return;
      try {
        slider.setPointerCapture(event.pointerId);
      } catch (error) {
        // Pointer capture is optional on older Safari versions.
      }
      modalPointerStart = {
        x: event.clientX,
        y: event.clientY,
        time: performance.now(),
        startedOnVideo: true
      };
      modalWasDragged = false;
    }, { passive: true });

    slider.addEventListener('pointermove', (event) => {
      if (!modalPointerStart || !event.isPrimary) return;
      const deltaX = event.clientX - modalPointerStart.x;
      const deltaY = event.clientY - modalPointerStart.y;
      const distanceX = Math.abs(deltaX);
      const distanceY = Math.abs(deltaY);
      if (distanceX > TAP_MOVE_THRESHOLD || distanceY > TAP_MOVE_THRESHOLD) {
        modalWasDragged = true;
      }

      if (distanceX > distanceY && modalWasDragged) {
        event.preventDefault();
        let visualDeltaX = deltaX;
        const draggingPastStart = current === 0 && deltaX > 0;
        const draggingPastEnd = current === slideContainers.length - 1 && deltaX < 0;
        if (draggingPastStart || draggingPastEnd) visualDeltaX *= 0.22;

        pendingModalDeltaX = visualDeltaX;
        if (modalDragFrame === null) {
          modalDragFrame = requestAnimationFrame(() => {
            modalDragFrame = null;
            slider.style.transition = 'none';
            slider.style.transform = `translate3d(calc(-${current * 100}% + ${pendingModalDeltaX}px), 0, 0)`;
          });
        }
      }
    }, { passive: false });

    slider.addEventListener('pointerup', (event) => {
      if (!modalPointerStart || !event.isPrimary) return;
      const deltaX = event.clientX - modalPointerStart.x;
      const deltaY = event.clientY - modalPointerStart.y;
      const elapsed = Math.max(1, performance.now() - modalPointerStart.time);
      const velocityX = Math.abs(deltaX) / elapsed;
      const isHorizontalSwipe = modalWasDragged
        && Math.abs(deltaX) > Math.abs(deltaY)
        && (
          Math.abs(deltaX) >= SWIPE_DISTANCE_THRESHOLD
          || velocityX >= SWIPE_VELOCITY_THRESHOLD
        );
      const shouldEnableSound = !modalWasDragged && modalPointerStart.startedOnVideo;

      if (modalDragFrame !== null) {
        cancelAnimationFrame(modalDragFrame);
        modalDragFrame = null;
        slider.style.transition = 'none';
        slider.style.transform = `translate3d(calc(-${current * 100}% + ${pendingModalDeltaX}px), 0, 0)`;
      }

      modalPointerStart = null;
      modalWasDragged = false;

      if (isHorizontalSwipe) {
        const nextIndex = deltaX < 0
          ? Math.min(current + 1, slideContainers.length - 1)
          : Math.max(current - 1, 0);
        const snapDuration = Math.max(160, SLIDE_SNAP_DURATION - Math.min(70, velocityX * 90));
        animateToSlide(nextIndex, Math.round(snapDuration));
      } else {
        slider.style.transition = 'transform 180ms cubic-bezier(0.16, 1, 0.3, 1)';
        slider.style.transform = `translate3d(-${current * 100}%, 0, 0)`;
        if (shouldEnableSound) enableActiveVideoSound();
      }
    }, { passive: true });

    slider.addEventListener('pointercancel', () => {
      if (modalDragFrame !== null) {
        cancelAnimationFrame(modalDragFrame);
        modalDragFrame = null;
      }
      modalPointerStart = null;
      modalWasDragged = false;
      slider.style.transition = 'transform 180ms cubic-bezier(0.16, 1, 0.3, 1)';
      slider.style.transform = `translate3d(-${current * 100}%, 0, 0)`;
    }, { passive: true });

    modal.addEventListener('click', (event) => {
      if (event.target === modal) closeModal();
    });
  };

  // --- Entry point ---
  const initAll = () => {
    document.querySelectorAll('[data-ugc-reels]').forEach(initBlock);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
